import type { RandomSource } from "@/core/rng";
import {
  DAILY_V6_PACING_LABEL,
  DAILY_V6_PRESSURE_PROFILES,
  DAILY_V6_SPEED_PROFILES,
  DAILY_V6_TIER_INTENSITY_RANGE,
  type DailyV6Archetype,
  type DailyV6PacingId,
  type DailyV6PressureCurve,
  type DailyV6PressureProfileId,
  type DailyV6SpeedProfileId,
  type DailyV6Tier,
} from "./types";

/** Section 24 - per-tier weighted pools for speed/pressure/pacing. These are HARD restrictions (never just "downweighted"), not merely low-probability - e.g. Redline is structurally absent from Normal's pool, not just rare, which is exactly what makes "Normal + Redline" (section 23's forbidden combo) impossible by construction rather than merely unlikely. */
type WeightedPool<T extends string> = ReadonlyArray<readonly [T, number]>;

const SPEED_POOL: Record<DailyV6Tier, WeightedPool<DailyV6SpeedProfileId>> = {
  accessible: [
    ["cruise", 0.65],
    ["standard", 0.35],
  ],
  normal: [
    ["cruise", 0.2],
    ["standard", 0.6],
    ["fast", 0.2],
  ],
  hard: [
    ["standard", 0.3],
    ["fast", 0.7],
  ],
  // Extreme's rare "geometry/mechanics-driven" alternative (section 9): a
  // day where speed is deliberately NOT a major difficulty driver still
  // needs a legal, tier-compatible profile - resolved as an extra draw
  // between cruise/standard only when this branch is picked (see
  // `pickSpeedProfileId()`).
  extreme: [
    ["fast", 0.45],
    ["redline", 0.45],
    ["standard", 0.1], // placeholder weight for the "alternative" branch; the alternative itself further rolls cruise/standard 50/50
  ],
};

const PRESSURE_POOL: Record<DailyV6Tier, WeightedPool<DailyV6PressureProfileId>> = {
  accessible: [
    ["relaxed", 0.7],
    ["standard", 0.3],
  ],
  normal: [
    ["relaxed", 0.2],
    ["standard", 0.65],
    ["aggressive", 0.15],
  ],
  hard: [
    ["standard", 0.25],
    ["aggressive", 0.6],
    ["relentless", 0.15],
  ],
  extreme: [
    ["aggressive", 0.45],
    ["relentless", 0.55],
  ],
};

const PACING_POOL: Record<DailyV6Tier, WeightedPool<DailyV6PacingId>> = {
  accessible: [
    ["slow_burn", 0.45],
    ["steady_climb", 0.55],
  ], // never front_loaded
  normal: [
    ["slow_burn", 0.3],
    ["steady_climb", 0.6],
    ["front_loaded", 0.1],
  ],
  hard: [
    ["slow_burn", 0.2],
    ["steady_climb", 0.55],
    ["front_loaded", 0.25],
  ],
  extreme: [
    ["slow_burn", 0.15],
    ["steady_climb", 0.45],
    ["front_loaded", 0.4],
  ],
};

function weightedPick<T extends string>(rng: RandomSource, pool: WeightedPool<T>): T {
  const total = pool.reduce((sum, [, w]) => sum + w, 0);
  let roll = rng() * total;
  for (const [value, weight] of pool) {
    roll -= weight;
    if (roll < 0) return value;
  }
  return pool[pool.length - 1][0];
}

/** Extreme's rare (~10%) "speed is not a major driver" branch resolves to cruise or standard, 50/50, via a SEPARATE draw from `rng` - so it still consumes exactly one deterministic draw either way (redline/fast vs. the alternative), never a variable number of draws depending on which branch wins. */
export function pickSpeedProfileId(tier: DailyV6Tier, rng: RandomSource): DailyV6SpeedProfileId {
  const picked = weightedPick(rng, SPEED_POOL[tier]);
  if (tier === "extreme" && picked === "standard") {
    // The "standard" pool weight for Extreme is the alternative-branch
    // placeholder (see `SPEED_POOL`'s doc) - resolve it into a real
    // cruise/standard choice with its own draw.
    return rng() < 0.5 ? "cruise" : "standard";
  }
  return picked;
}

export function pickPressureProfileId(tier: DailyV6Tier, rng: RandomSource): DailyV6PressureProfileId {
  return weightedPick(rng, PRESSURE_POOL[tier]);
}

export function pickPacingId(tier: DailyV6Tier, rng: RandomSource): DailyV6PacingId {
  return weightedPick(rng, PACING_POOL[tier]);
}

export function pickIntensity(tier: DailyV6Tier, rng: RandomSource): number {
  const { min, max } = DAILY_V6_TIER_INTENSITY_RANGE[tier];
  return min + rng() * (max - min);
}

/** Section 12 - starting length ranges per tier (a SECONDARY modifier, never itself a major driver). */
const STARTING_LENGTH_RANGE: Record<DailyV6Tier, { min: number; max: number }> = {
  accessible: { min: 3, max: 4 },
  normal: { min: 3, max: 5 },
  hard: { min: 4, max: 6 },
  extreme: { min: 4, max: 7 },
};

/** The minimum (safest) starting length for `tier` - exported for `generatorV6.ts`'s per-tier fallback, which always picks the safest legal value in every dimension rather than a typical/average one. */
export function minStartingLengthFor(tier: DailyV6Tier): number {
  return STARTING_LENGTH_RANGE[tier].min;
}

/**
 * Section 25's "deterministic fallback appropriate to the chosen tier" -
 * the single SAFEST legal `(speed, pressure, pacing)` combination for
 * each tier, i.e. the slowest speed profile and gentlest pressure/pacing
 * that tier's own hard-restricted pool (`SPEED_POOL`/`PRESSURE_POOL`/
 * `PACING_POOL` above) allows. Never the "typical" or weighted-average
 * pick - a fallback exists to be trivially, unquestionably safe, not
 * representative. `pacingId` is always `slow_burn` (the gentlest
 * modifier - extends grace, stretches the ramp - present in every
 * tier's pool). `hard`/`extreme`'s pools never include `cruise` speed
 * (hard) or `relaxed`/`standard` pressure (extreme) the way lower tiers
 * do, so their safest options (`standard` speed for hard; `cruise` speed
 * via extreme's own rare "alternative" branch, `aggressive` pressure for
 * extreme) are still genuinely tier-legal, never borrowed from another
 * tier's pool.
 */
export const SAFEST_PROFILES_BY_TIER: Record<DailyV6Tier, { speedProfileId: DailyV6SpeedProfileId; pressureProfileId: DailyV6PressureProfileId; pacingId: DailyV6PacingId }> = {
  accessible: { speedProfileId: "cruise", pressureProfileId: "relaxed", pacingId: "slow_burn" },
  normal: { speedProfileId: "cruise", pressureProfileId: "relaxed", pacingId: "slow_burn" },
  hard: { speedProfileId: "standard", pressureProfileId: "standard", pacingId: "slow_burn" },
  extreme: { speedProfileId: "cruise", pressureProfileId: "aggressive", pacingId: "slow_burn" },
};

/** The minimum (safest) continuous intensity for `tier` - see `DAILY_V6_TIER_INTENSITY_RANGE`. */
export function minIntensityFor(tier: DailyV6Tier): number {
  return DAILY_V6_TIER_INTENSITY_RANGE[tier].min;
}

/** Dense-geometry archetypes (Maze/Lanes/Hybrid) bias away from the tier's own highest starting length - never automatically choosing the upper bound (section 12). Modeled as a simple triangular bias toward the low end (average of two draws) rather than uniform, for every dense archetype. */
const DENSE_ARCHETYPES: ReadonlySet<DailyV6Archetype> = new Set(["maze", "lanes", "hybrid"]);

export function pickStartingLength(tier: DailyV6Tier, archetype: DailyV6Archetype, rng: RandomSource): number {
  const { min, max } = STARTING_LENGTH_RANGE[tier];
  const span = max - min;
  if (span <= 0) return min;
  if (DENSE_ARCHETYPES.has(archetype)) {
    const a = rng();
    const b = rng();
    return min + Math.round(Math.min(a, b) * span);
  }
  return min + Math.floor(rng() * (span + 1));
}

/** Section 19 - BLACK HOLE caps per tier (caps, not goals). */
export const BLACK_HOLE_CAP: Record<DailyV6Tier, number> = { accessible: 0, normal: 1, hard: 2, extreme: 3 };

/** Section 17 - fixed portal pair caps per tier. */
export const PORTAL_PAIR_CAP: Record<DailyV6Tier, number> = { accessible: 1, normal: 1, hard: 2, extreme: 2 };

/** Section 10 - resolves a chosen speed/pressure profile + pacing modifier into the final continuous curve `LevelGame` runs (`computeCurvePressureTargetStepMs`, SpeedEngine.ts). Pacing modifies grace/ramp only (section 11) - `finalTargetStepMs`/`ceilingMs` come from the speed+pressure profile alone. */
export function resolvePressureCurve(speedProfileId: DailyV6SpeedProfileId, pressureProfileId: DailyV6PressureProfileId, pacingId: DailyV6PacingId): DailyV6PressureCurve {
  const speed = DAILY_V6_SPEED_PROFILES[speedProfileId];
  const pressure = DAILY_V6_PRESSURE_PROFILES[pressureProfileId];

  let graceMs = pressure.graceMs;
  let rampMs = pressure.rampMs;
  if (pacingId === "slow_burn") {
    graceMs += 10_000;
    rampMs *= 1.25;
  } else if (pacingId === "front_loaded") {
    graceMs = Math.max(8_000, graceMs - 5_000);
    rampMs *= 0.8;
  }
  // "steady_climb": no modification.

  return {
    graceMs,
    rampMs,
    finalTargetStepMs: speed.ceilingMs + pressure.finalTargetOffsetMs,
    ceilingMs: speed.ceilingMs,
  };
}

export function pacingLabel(pacingId: DailyV6PacingId): string {
  return DAILY_V6_PACING_LABEL[pacingId];
}

/** Concise, honest pre-run copy for V6's own pressure profile - mirrors `describePressureRamp()` (`pressure.ts`, V1-V5's own ramp table) for the pre-run screen, never a raw dump of `pressureCurve`'s internal ms fields. */
export function describeV6Pressure(pressureProfileId: DailyV6PressureProfileId): string {
  switch (pressureProfileId) {
    case "relaxed":
      return "Speed creeps up very gradually the longer you survive without growing.";
    case "standard":
      return "Speed ramps up at a steady pace the longer you survive without growing.";
    case "aggressive":
      return "Speed ramps up quickly if you stall without growing - keep eating.";
    case "relentless":
      return "Speed relentlessly climbs toward its ceiling - never stop moving.";
  }
}
